import type { AgentWorkspace } from 'v2/shared/utils/role-based-visibility'
import type { RSandboxNumber, RSandboxSignupNumber } from 'v2/types/api/settings'

/** The two directions the guided setup covers. */
export enum SandboxDirection {
  inbound = 'inbound',
  outbound = 'outbound',
}

/** What answers (inbound) or drives (outbound) the call. */
export enum SandboxConnectKind {
  agent = 'agent',
  stream = 'stream',
  sip = 'sip',
}

export interface SandboxDirectionCopy {
  heading: string
  chooseTitle: string
  chooseSubtitle: string
  finalTitle: string
  finalSubtitle: string
  options: { kind: SandboxConnectKind; title: string; description: string }[]
}

/**
 * The agent option names what it builds: an AI agent, or a Flow when the org has agents off (the Flow
 * builder allows no AI nodes).
 */
const AGENT_COPY: Record<
  AgentWorkspace,
  { title: string } & Record<SandboxDirection, string> & { connect: SandboxConnectCopy }
> = {
  agents: {
    title: 'AI Agent',
    [SandboxDirection.inbound]: 'Your Plivo AI agent answers the call.',
    [SandboxDirection.outbound]: 'A Plivo agent talks to the callee.',
    connect: {
      title: 'Connect an AI Agent',
      subtitle: 'Pick the agent that answers the call.',
      empty: 'No agents yet, create one to continue.',
      createLabel: 'Build an agent',
      connected: 'Your AI agent',
    },
  },
  flow: {
    title: 'Flow',
    [SandboxDirection.inbound]: 'A Plivo flow answers the call.',
    [SandboxDirection.outbound]: 'A Plivo flow handles the call.',
    connect: {
      title: 'Connect a flow',
      subtitle: 'Pick the flow that answers the call.',
      empty: 'No flows yet, create one to continue.',
      createLabel: 'Build a flow',
      connected: 'Your flow',
    },
  },
}

const agentOption = (direction: SandboxDirection, workspace: AgentWorkspace) => ({
  kind: SandboxConnectKind.agent,
  title: AGENT_COPY[workspace].title,
  description: AGENT_COPY[workspace][direction],
})

/**
 * Copy differs between the two tabs by more than a word - the options describe
 * opposite call directions - so it is tabulated rather than interpolated.
 *
 * Order is bring-your-own-stack first, no-code agent last, in every region
 * (product decision 2026-09-17): the sandbox is reached by developers wiring an
 * existing setup to a trial number, and the agent tile led purely because it was
 * written first. The array IS the order - nothing sorts or ranks it - so a tile
 * moved here moves on screen, and the order is pinned per direction in
 * sandbox-setup-copy.test.ts.
 */
export const getDirectionCopy = (
  direction: SandboxDirection,
  workspace: AgentWorkspace = 'agents',
): SandboxDirectionCopy =>
  direction === SandboxDirection.inbound
    ? {
        heading: 'Receive a call on your sandbox number',
        chooseTitle: 'Choose what answers the call',
        chooseSubtitle: 'Connect your own setup, or use a Plivo demo app to test.',
        finalTitle: 'Ring your sandbox number',
        finalSubtitle: 'Call it from a verified number, then check Logs.',
        options: [
          {
            kind: SandboxConnectKind.stream,
            title: 'Audio stream',
            description: 'Stream call audio to your server.',
          },
          {
            kind: SandboxConnectKind.sip,
            title: 'SIP trunk',
            description: 'Route the call over your SIP trunk.',
          },
          agentOption(direction, workspace),
        ],
      }
    : {
        heading: 'Place a call from your sandbox number',
        chooseTitle: 'Choose what drives the call',
        chooseSubtitle: 'Connect your own setup, or use a Plivo demo app to test.',
        finalTitle: 'Place the call',
        finalSubtitle: 'Once the call goes out from your sandbox number, check logs.',
        options: [
          {
            kind: SandboxConnectKind.stream,
            title: 'Audio stream',
            description: 'Stream call audio to your server.',
          },
          // No SIP option outbound: a number's only trunk linkage is the
          // inbound one (`trunk_id`, an InboundTrunkSelect in the configure
          // form), so a trunk has nothing to attach to on this tab.
          agentOption(direction, workspace),
        ],
      }

/**
 * "+ Add (N left)" on step 1. Undefined once full, so the caller renders no
 * affordance rather than an "Add (0 left)" that cannot do anything.
 */
export const getAddLeftLabel = (verifiedCount: number, cap?: number): string | undefined => {
  if (cap === undefined) return '+ Add'
  const left = cap - verifiedCount
  return left > 0 ? `+ Add (${left} left)` : undefined
}

/**
 * The numbers listed under step 1: every number that can currently exchange
 * calls with the sandbox. The sign-up number belongs here - it is already
 * callable - even though it consumes no cap slot.
 */
export const getCallableNumbers = (
  verified: RSandboxNumber[],
  signupNumber?: RSandboxSignupNumber,
): string[] => [
  ...(signupNumber?.phone_number ? [signupNumber.phone_number] : []),
  ...verified.map((row) => row.phone_number),
]

/**
 * "Remaining credits: X of Y", or just "X" when the trial grant total is
 * unknown.
 *
 * The design shows "₹210 of ₹250", but only the balance has a source today -
 * the grant is an AMS prod-config value with no API. Rather than hardcode a
 * denominator that finance can change without touching this repo, the total is
 * omitted until something can supply it.
 */
export const getCreditsLabel = (balance?: string, grantTotal?: string): string | undefined => {
  if (!balance) return undefined
  return grantTotal ? `${balance} of ${grantTotal}` : balance
}

/**
 * Whether this role may use each step-2 option.
 *
 * Three guards, all necessary:
 *
 * - `canEditCalls` (`channelCalls.edit`) because connecting PATCHes the phone
 *   number's configuration. The sandbox gate itself only asks for
 *   `channelCalls.view`, so without this a read-only calls user got a fully
 *   actionable picker and then a forbidden write. Same permission the
 *   Configure Number save is gated on.
 * - `canBuildAgents` (AI Agents, or Telephony > Flow when the org has agents off)
 *   for the agent option.
 * - `canViewBuild` (the BUILD section) for applications and trunks, whose
 *   "Create ..." links land there.
 *
 * An option the role cannot use is not offered at all, rather than opening a
 * picker whose Connect button 403s.
 */
export const getConnectPermissions = (
  canViewBuild: boolean,
  canBuildAgents: boolean,
  canEditCalls: boolean,
): Record<SandboxConnectKind, boolean> => ({
  [SandboxConnectKind.agent]: canEditCalls && canBuildAgents,
  [SandboxConnectKind.stream]: canEditCalls && canViewBuild,
  [SandboxConnectKind.sip]: canEditCalls && canViewBuild,
})

export interface SandboxConnectCopy {
  title: string
  subtitle: string
  /** Shown in place of the list when the account has none of this kind yet. */
  empty: string
  createLabel: string
  /** Success-toast subject, e.g. "Your AI agent is now answering...". */
  connected: string
}

/**
 * Copy for the three Connect dialogs, verbatim from the v2 design.
 *
 * Not direction-aware: the design specifies one set of strings, drawn for the
 * inbound tab. "receives the call" reads slightly off under Outbound - raised
 * with design rather than invented here.
 */
export const getConnectCopy = (
  kind: SandboxConnectKind,
  workspace: AgentWorkspace = 'agents',
): SandboxConnectCopy =>
  ({
    [SandboxConnectKind.agent]: AGENT_COPY[workspace].connect,
    [SandboxConnectKind.stream]: {
      title: 'Connect an application',
      subtitle: 'Pick the app that streams call audio.',
      empty: 'No applications yet, create one to continue.',
      createLabel: 'Create an application',
      connected: 'Your application',
    },
    [SandboxConnectKind.sip]: {
      title: 'Connect a SIP trunk',
      subtitle: 'Pick the trunk that receives the call.',
      empty: 'No trunks yet, create one to continue.',
      createLabel: 'Create a trunk',
      connected: 'Your SIP trunk',
    },
  })[kind]
